export const parseAndHandle = <T,>(content: string, callback: (modal: T) => void, errorHandler: (error: SyntaxError) => void) => {
    try {
        const model = JSON.parse(content) as T;
        callback(model);
    }
    catch (error) {
        errorHandler(error as SyntaxError);
    }
};

/**
 * Closes incomplete JSON fragments to make them valid and parseable.
 * 
 * Perfect for streaming JSON data from LLMs, APIs, or any source that delivers
 * JSON in fragments. Automatically completes incomplete structures while
 * preserving the original semantic meaning.
 * 
 * Features:
 * - Handles escaped quotes and backslashes correctly
 * - Completes incomplete object keys with ": null"
 * - Finishes partial primitives (tr → true, fal → false, nul → null)
 * - Manages nested objects and arrays of any depth
 * - Removes trailing commas
 * - Optimized for large objects and high-performance scenarios
 * 
 * @param fragment - Incomplete JSON string from streaming source
 * @returns Complete, valid JSON string ready for JSON.parse()
 * 
 * @example
 * ```typescript
 * // Basic usage
 * closeJsonFragment('{"name": "John')           // → '{"name": "John"}'
 * closeJsonFragment('[1, 2, 3')                 // → '[1, 2, 3]'
 * closeJsonFragment('{"flag": tr')              // → '{"flag": true}'
 * 
 * // LLM streaming scenarios
 * closeJsonFragment('{"response": {"text": "Hello world')  
 * // → '{"response": {"text": "Hello world"}}'
 * 
 * // Complex nested structures
 * closeJsonFragment('{"users": [{"id": 1, "profile": {"name"')
 * // → '{"users": [{"id": 1, "profile": {"name": null}}]}'
 * ```
 */
export function closeJsonFragment(fragment: string): string {
    // Handle edge cases: empty or whitespace-only strings
    if (!fragment || fragment.trim() === '') {
        return fragment;
    }

    let stack: string[] = [];
    let inString = false;
    let i = 0;
    let lastNonWhitespaceChar = '';
    let lastNonWhitespaceIndex = -1;

    // Parse through the fragment character by character
    while (i < fragment.length) {
        const c = fragment[i];

        if (inString) {
            // Inside a string - only look for unescaped quotes
            if (c === '"') {
                // Count consecutive backslashes before this quote
                let backslashCount = 0;
                let j = i - 1;
                while (j >= 0 && fragment[j] === '\\') {
                    backslashCount++;
                    j--;
                }
                // If even number of backslashes (including 0), quote is not escaped
                if (backslashCount % 2 === 0) {
                    inString = false;
                }
            }
        } else {
            // Outside string - track structural elements
            if (c === '"') {
                inString = true;
            } else if (c === '{') {
                stack.push('}');
            } else if (c === '[') {
                stack.push(']');
            } else if (c === '}') {
                if (stack.length > 0 && stack[stack.length - 1] === '}') {
                    stack.pop();
                }
            } else if (c === ']') {
                if (stack.length > 0 && stack[stack.length - 1] === ']') {
                    stack.pop();
                }
            }

            // Track last non-whitespace character for context
            if (!/\s/.test(c)) {
                lastNonWhitespaceChar = c;
                lastNonWhitespaceIndex = i;
            }
        }
        i++;
    }

    // Special case: if we're in a string but the string is just an opening quote
    // at the very end (like '{"'), treat it as an incomplete object instead
    if (inString && lastNonWhitespaceChar === '"' && lastNonWhitespaceIndex === fragment.length - 1) {
        // Check if the quote immediately follows { or ,
        const beforeQuote = fragment.substring(0, lastNonWhitespaceIndex).trim();
        if (beforeQuote.endsWith('{') || beforeQuote.endsWith(',')) {
            // This is likely an incomplete object, not a string
            inString = false;
            // Remove the trailing quote as it's not actually starting a string
            fragment = fragment.substring(0, lastNonWhitespaceIndex);
        }
    }

    // If we're still inside a string, close it properly
    if (inString) {
        // Handle strings ending with backslashes (need to escape them)
        let backslashCount = 0;
        let j = fragment.length - 1;
        while (j >= 0 && fragment[j] === '\\') {
            backslashCount++;
            j--;
        }
        // If odd number of backslashes, add another to escape the closing quote
        if (backslashCount % 2 === 1) {
            fragment += '\\';
        }
        fragment += '"';
    }

    // Handle incomplete object keys and values
    if (!inString && stack.length > 0 && stack[stack.length - 1] === '}') {
        if (lastNonWhitespaceChar === ':') {
            // We have "key": but no value - add null
            fragment += ' null';
        } else {
            // Check if we have an incomplete key (quoted string at end)
            const match = fragment.match(/"([^"\\]|\\.)*"$/);
            if (match) {
                const matchedString = match[0];
                const stringStartPos = fragment.length - matchedString.length;
                const beforeString = fragment.substring(0, stringStartPos).replace(/\s+$/, '');

                // If preceded by { or , then it's likely an incomplete key
                if (beforeString.endsWith('{') || beforeString.endsWith(',')) {
                    fragment += ': null';
                }
            }
        }
    }

    // Complete partial primitive values (true, false, null)
    if (!inString) {
        const match = fragment.match(/([a-zA-Z]*)$/);
        const lastWord = match ? match[1] : "";
        for (const keyword of ['true', 'false', 'null']) {
            if (lastWord && keyword.startsWith(lastWord)) {
                fragment += keyword.slice(lastWord.length);
                break;
            }
        }
    }

    // Remove trailing commas (invalid in JSON)
    fragment = fragment.replace(/,(\s*)([\]}])/g, '$1$2');
    fragment = fragment.replace(/,\s*$/, '');

    // Close all remaining open structures
    while (stack.length > 0) {
        fragment += stack.pop();
    }

    return fragment;
}

// Export for use in modules
// export { closeJsonFragment };

// // Type definitions for better TypeScript support
// export interface JsonFragmentOptions {
//     /** Custom null replacement for incomplete values (default: null) */
//     nullReplacement?: any;
//     /** Whether to preserve formatting/whitespace (default: false) */
//     preserveFormatting?: boolean;
// }

// /**
//  * Extended version with options (future enhancement)
//  */
// export function closeJsonFragmentWithOptions(
//     fragment: string,
//     options: JsonFragmentOptions = {}
// ): string {
//     // For now, just use the standard implementation
//     // Can be extended in the future for custom behavior
//     return closeJsonFragment(fragment);
// }

// // Example usage patterns for documentation
// const examples = {
//     // Basic incomplete structures
//     basicString: () => closeJsonFragment('"hello'),                    // → "hello"
//     basicObject: () => closeJsonFragment('{"key"'),                   // → {"key": null}
//     basicArray: () => closeJsonFragment('[1, 2'),                     // → [1, 2]

//     // Primitive completion
//     booleanTrue: () => closeJsonFragment('{"active": tr'),            // → {"active": true}
//     booleanFalse: () => closeJsonFragment('{"visible": fal'),         // → {"visible": false}
//     nullValue: () => closeJsonFragment('{"data": nul'),               // → {"data": null}

//     // String edge cases
//     escapedQuotes: () => closeJsonFragment('{"text": "Say \\"hi\\"'), // → {"text": "Say \"hi\""}
//     trailingBackslash: () => closeJsonFragment('{"path": "C:\\\\'),   // → {"path": "C:\\\\"}

//     // Complex nested structures
//     nestedObjects: () => closeJsonFragment('{"user": {"profile": {"name"'),
//     // → {"user": {"profile": {"name": null}}}

//     arrayOfObjects: () => closeJsonFragment('[{"id": 1}, {"name"'),
//     // → [{"id": 1}, {"name": null}]

//     // Real-world LLM streaming scenarios
//     chatResponse: () => closeJsonFragment('{"choices": [{"message": {"content": "Hello'),
//     // → {"choices": [{"message": {"content": "Hello"}}]}

//     functionCall: () => closeJsonFragment('{"function_call": {"name": "calculate", "arguments": "{\\"x\\": 5'),
//     // → {"function_call": {"name": "calculate", "arguments": "{\"x\": 5"}}

//     // Large object simulation
//     largeUserData: () => closeJsonFragment(
//         '{"users": [' +
//         '{"id": 1, "name": "Alice", "email": "alice@example.com", "profile": {"age": 30}}, ' +
//         '{"id": 2, "name": "Bob", "email"'
//     ),
//     // → {"users": [{"id": 1, "name": "Alice", "email": "alice@example.com", "profile": {"age": 30}}, {"id": 2, "name": "Bob", "email": null}]}
// };

// // Performance benchmark helper
// export function benchmarkCloseJsonFragment(iterations = 10000): void {
//     const testCases = [
//         '{"simple": "test',
//         '{"nested": {"deep": {"very": {"deep": {"object"',
//         '[1, 2, 3, {"mixed": "content", "numbers": [4, 5',
//         '{"large": "' + 'x'.repeat(1000) + '"incomplete text here'
//     ];

//     //console.log`🚀 Benchmarking closeJsonFragment with ${iterations} iterations...`);

//     testCases.forEach((testCase, index) => {
//         const start = performance.now();

//         for (let i = 0; i < iterations; i++) {
//             closeJsonFragment(testCase);
//         }

//         const end = performance.now();
//         const avgTime = (end - start) / iterations;

//         //console.log`Test ${index + 1}: ${avgTime.toFixed(4)}ms per call (${testCase.substring(0, 30)}...)`);
//     });
// }

// // Quick validation helper
// export function validateJsonFragment(fragment: string): {
//     isValid: boolean;
//     completed: string;
//     error?: string;
// } {
//     try {
//         const completed = closeJsonFragment(fragment);
//         JSON.parse(completed);
//         return { isValid: true, completed };
//     } catch (error: any) {
//         return {
//             isValid: false,
//             completed: closeJsonFragment(fragment),
//             error: error.message
//         };
//     }
// }

// /**
//  * Browser/Node.js compatibility
//  */
// if (typeof window !== 'undefined') {
//     // Browser environment
//     (window as any).closeJsonFragment = closeJsonFragment;
// } else if (typeof global !== 'undefined') {
//     // Node.js environment
//     (global as any).closeJsonFragment = closeJsonFragment;
// }

// // Default export for convenience
// export default closeJsonFragment;